import { z } from 'zod';
import { zId, zMoney } from './primitives.js';

/** PY-09 */
export const zProofStatus = z.enum(['PENDING', 'VERIFIED', 'REJECTED']);

/**
 * PY-12 — one proof allocated across several invoices. The API refuses an
 * allocation that does not reconcile; see money.allocate().
 */
export const zPaymentAllocation = z.object({
  invoiceId: zId,
  amount: zMoney,
});

export const zRecordPayment = z.object({
  amount: zMoney,
  currency: z.string().length(3),
  paidOn: z.string().date(),
  bankReference: z.string().max(64).optional(),
  allocations: z.array(zPaymentAllocation).min(1),
});
export type RecordPayment = z.infer<typeof zRecordPayment>;

/**
 * PY-07/PY-08 — a retailer's payment proof, settled directly against an
 * order rather than allocated across invoices. Deliberately narrower than
 * `zRecordPayment` above: that model needs invoices, which need a
 * shipment/dispatch pipeline that does not exist yet. See
 * `db/schema/payment.ts`'s own comment for the full reasoning. Moving to
 * `zRecordPayment` once invoices exist is real, separate, future work.
 */
export const zSubmitPaymentProof = z.object({
  amount: zMoney,
  currency: z.string().length(3),
  paidOn: z.string().date(),
  bankReference: z.string().max(64).optional(),
  /** Milestone payments: the consignment invoice this payment is for. Omitted = order-level money.
   *  The FIELD name is renamed in Wave 3 — see `consignment-invoice.ts`. */
  consignmentInvoiceId: z.string().uuid().optional(),
});
export type SubmitPaymentProof = z.infer<typeof zSubmitPaymentProof>;

export const zRejectPaymentProof = z.object({ reason: z.string().min(3).max(1000) });
